// Sidebar shown when the Locations page is in "all sites" mode —
// just a clean list of every site, click-to-drill. Filtering is done
// by the parent so the same `sites` array reaches the map; we just
// render. A `getColor` prop drives the per-row colour swatch when
// the user has chosen a "Color by" dimension (entity / state / …).

import { useMemo } from 'react'
import { Box, Stack, Typography, alpha } from '@mui/material'
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded'
import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { Virtuoso } from 'react-virtuoso'

import type { Site } from '@cmms/types'
import { formatSiteName } from '../../../lib/siteDisplayName'
import type { ColorBy } from '../lib/siteColors'
import { getDimensionValue } from '../lib/siteColors'

interface Props {
  /** Pre-filtered list — parent applies the search filter. */
  sites: Site[]
  /** Original (unfiltered) total — used to show "X of Y" when searching. */
  totalCount: number
  selectedSiteId: string | null
  onSelectSite: (siteId: string) => void
  /** Active "Color by" dimension — only used to cluster rows; the
   *  legend itself is drawn as an overlay on the map. */
  colorBy: ColorBy
  /** Returns the colour to use for a site, or undefined if color-by=none. */
  getColor?: (site: Site) => string | undefined
}

export function AllSitesSidebar({
  sites, totalCount, selectedSiteId, onSelectSite,
  colorBy, getColor,
}: Props) {
  // Show "has coordinates" sites first so users see what's actually
  // on the map at the top of the list. Within that, sort by colour
  // group when color-by is on so same-coloured sites cluster.
  const sorted = useMemo(() => {
    return [...sites].sort((a, b) => {
      const aHas = a.latitude != null && a.longitude != null
      const bHas = b.latitude != null && b.longitude != null
      if (aHas !== bHas) return aHas ? -1 : 1
      if (colorBy !== 'none') {
        const av = getDimensionValue(a, colorBy) ?? '￿'  // empty sinks last
        const bv = getDimensionValue(b, colorBy) ?? '￿'
        const cmp = av.localeCompare(bv)
        if (cmp !== 0) return cmp
      }
      return a.name.localeCompare(b.name)
    })
  }, [sites, colorBy])

  const isFiltered = sites.length !== totalCount

  return (
    <Box sx={{
      width: { xs: '100%', md: 320 },
      flexShrink: 0,
      borderRight: { md: '1px solid' },
      borderBottom: { xs: '1px solid', md: 'none' },
      borderColor: { xs: 'divider', md: 'divider' },
      display: 'flex', flexDirection: 'column',
      minHeight: 0,
      bgcolor: 'background.paper',
    }}>
      <Box sx={{
        px: 2, py: 1.5,
        borderBottom: '1px solid', borderColor: 'divider',
        background: (t) => t.palette.mode === 'light'
          ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.045)} 0%, transparent 100%)`
          : `linear-gradient(180deg, ${alpha(t.palette.common.white, 0.04)} 0%, transparent 100%)`,
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7,
            textTransform: 'uppercase', color: 'text.disabled' }}>
            All sites
          </Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 700,
            color: isFiltered ? 'primary.main' : 'text.disabled' }}>
            {sorted.length}{isFiltered ? ` / ${totalCount}` : ''}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {sorted.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.disabled' }}>
            <Typography sx={{ fontSize: 12.5 }}>
              {isFiltered ? 'No sites match the search.' : 'No sites yet.'}
            </Typography>
          </Box>
        ) : (
          // Virtualized via react-virtuoso — with ~1400 sites, mounting
          // every row as a real DOM node made this list slow to
          // reconcile on every click (same fix as AssetList/RequestsPage).
          <Virtuoso
            style={{ height: '100%' }}
            data={sorted}
            increaseViewportBy={200}
            itemContent={(idx, s) => {
              const hasCoords = s.latitude != null && s.longitude != null
              const isSelected = s.id === selectedSiteId
              const dimColor = getColor?.(s)
              return (
                <Box
                  role="button"
                  onClick={() => onSelectSite(s.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    px: 2, py: 1.5,
                    cursor: 'pointer',
                    borderTop: idx === 0 ? 'none' : '1px solid',
                    borderColor: 'divider',
                    bgcolor: isSelected
                      ? (t) => alpha(t.palette.primary.main, 0.08)
                      : 'transparent',
                    borderLeft: '3px solid',
                    borderLeftColor: isSelected
                      ? 'primary.main'
                      : (dimColor ?? 'transparent'),
                    transition: 'background-color 120ms ease',
                    '&:hover': {
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <Box sx={{
                    width: 28, height: 28, borderRadius: 1, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: dimColor
                      ? alpha(dimColor, 0.18)
                      : hasCoords
                        ? (t) => alpha(t.palette.primary.main, 0.12)
                        : 'action.hover',
                    color: dimColor ?? (hasCoords ? 'primary.main' : 'text.disabled'),
                  }}>
                    {hasCoords
                      ? <LocationOnRoundedIcon sx={{ fontSize: 16 }} />
                      : <LocationOffOutlinedIcon sx={{ fontSize: 16 }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatSiteName(s)}
                    </Typography>
                    {s.address && (
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.address}
                      </Typography>
                    )}
                  </Box>
                  <ChevronRightRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                </Box>
              )
            }}
          />
        )}
      </Box>
    </Box>
  )
}
